import { motion } from 'framer-motion';
import { Bike, Fish, TreePine, MapPin, Castle, Church, Landmark, Activity } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const activities = [
  {
    icon: Fish,
    titleKey: 'exploreOutdoorsTitle',
    descKey: 'exploreOutdoorsDesc',
    links: [
      { labelKey: 'linkFishing', url: 'https://www.visit-somme.com/active-great-outdoors/going-fishing' },
      { labelKey: 'linkGoKarting', url: 'https://goo.gl/maps/majkj2v2uKCnbCLE7' },
      { labelKey: 'linkKayaking', url: 'https://www.visit-somme.com/active-great-outdoors/take-canoe-kayak-or-raft-deep-nature' },
    ],
  },
  {
    icon: Bike,
    titleKey: 'bikeMontTitle',
    descKey: 'bikeMontDesc',
    links: [
      { labelKey: 'linkVeloscenic', url: 'https://www.veloscenic.com/' },
    ],
  },
  {
    icon: TreePine,
    titleKey: 'natureWalksTitle',
    descKey: 'natureWalksDesc',
    links: [
      { labelKey: 'linkTrailMap', url: 'https://www.ville-saint-lubin-des-joncherets.fr/media/243/download/Balade%20des%20Crampons.pdf?v=1' },
    ],
  },
  {
    icon: Activity,
    titleKey: 'tennisTitle',
    descKey: 'tennisDesc',
    links: [],
  },
];

const dayTrips = [
  {
    icon: Church,
    titleKey: 'evreuxTitle',
    descKey: 'evreuxDesc',
    distanceKey: 'evreux30min',
  },
  {
    icon: Landmark,
    titleKey: 'versaillesTitle',
    descKey: 'versaillesDesc',
    distanceKey: 'versailles60min',
  },
  {
    icon: Castle,
    titleKey: 'verneuiltTitle',
    descKey: 'verneuilDesc',
    distanceKey: 'verneuil20min',
  },
];

export const WhereToVisitPage = () => {
  const { t } = useLanguage();

  return (
    <section className="pt-14 pb-24 bg-[#FAFAFA]" data-testid="where-to-visit-page">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-serif text-4xl sm:text-5xl text-[#2D2A26] mb-4">
            {t('whereToVisitTitle')}
          </h2>
          <div className="w-12 h-0.5 bg-[#B5985A] mx-auto mb-6" />
          <p className="text-[#666] max-w-2xl mx-auto leading-relaxed">
            {t('whereToVisitDesc')}
          </p>
        </motion.div>

        {/* Activities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-16"
        >
          <h3 className="font-serif text-2xl text-[#2D2A26] mb-8 text-center">
            {t('groundsActivitiesTitle')}
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {activities.map((act, i) => {
              const Icon = act.icon;
              return (
                <motion.div
                  key={act.titleKey}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-white border border-[#E5E5E5] rounded-sm p-6 hover:shadow-sm transition-shadow"
                  data-testid={`activity-card-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#F5F0E8] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon className="w-5 h-5 text-[#B5985A]" />
                    </div>
                    <div>
                      <h4 className="font-serif text-lg text-[#2D2A26] mb-2">
                        {t(act.titleKey)}
                      </h4>
                      <p className="text-[#666] text-sm leading-relaxed mb-3">
                        {t(act.descKey)}
                      </p>
                      {act.links.length > 0 && (
                        <div className="flex flex-wrap gap-3">
                          {act.links.map((link) => (
                            <a
                              key={link.url}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#B5985A] text-sm hover:text-[#9A7F48] underline underline-offset-4 transition-colors"
                            >
                              {t(link.labelKey)}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Day Trips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h3 className="font-serif text-2xl text-[#2D2A26] mb-8 text-center">
            {t('dayTripsTitle')}
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {dayTrips.map((trip, i) => {
              const Icon = trip.icon;
              return (
                <motion.div
                  key={trip.titleKey}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-white border border-[#E5E5E5] rounded-sm p-6 text-center hover:shadow-sm transition-shadow"
                  data-testid={`day-trip-card-${i}`}
                >
                  <div className="w-12 h-12 rounded-full bg-[#F5F0E8] flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-[#B5985A]" />
                  </div>
                  <h4 className="font-serif text-lg text-[#2D2A26] mb-2">
                    {t(trip.titleKey)}
                  </h4>
                  <p className="text-[#666] text-sm leading-relaxed mb-3">
                    {t(trip.descKey)}
                  </p>
                  <span className="inline-flex items-center gap-1 text-xs text-[#999] bg-[#F5F0E8] px-3 py-1 rounded-full">
                    <MapPin className="w-3 h-3" />
                    {t(trip.distanceKey)}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
